
<template>
  <label class="text-white" :for="id">
      <span class="block m-t-b">{{ label }}</span>
      <input
        class="form-input"
        :class="{ 'error': !!errorMessage}"
        :name="name"
        :id="id"
        :required="required"
        :autocomplete="autocomplete"
        :type="type"
        :readonly="readonly"
        :aria-label="arialabel"
        :aria-describedby="'texterror' + id"
        :aria-invalid="!!errorMessage"
        v-on="handlers" 
        :value="value"
        :pattern="props.requiredPattern"
      />
      <span v-if="helper !=''" class="compl" aria-hidden="true">{{ helper }}</span>
      <span :id="'texterror' + id" class="error-txt">
      <span v-if="errorMessage">{{ errorMessage }}</span>
      </span>
  </label>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useField } from 'vee-validate';
import { modes } from '@/validation/interactionModes';

const props = defineProps({
  type: {
    type: String,
    default: "text",
  },
  defaultValue: {
    type: String,
    default: "",
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  arialabel: {
    type: String,
    required: false,
  },
  label: {
    type: String,
    required: true,
  },
  autocomplete: {
    type: String,
    required: false,
  },
  mode: {
    type: String,
    default: 'aggressive',
  },
  required: { 
    type: Boolean,
    default: false
  },
  helper: {
    type: String,
    default: "",
    required: false,
  },
  requiredPattern: {
    type: String,
    required: false,
  },
});

const { meta, value, errorMessage, handleChange, handleBlur } = useField(
  toRef(props, 'name'),
  null,
  {
    validateOnValueUpdate: false,
  }
);

// Workaround för att sätta defaultvärde på inputfältet.
value.value = props.defaultValue;

// generates the listeners
const handlers = computed(() => {
  const on = {
    blur: handleBlur,
    // default input event to sync the value
    // the `false` here prevents validation
    input: [(e) => handleChange(e, false)],
  };

  // Get list of validation events based on the current mode
  const triggers = modes[props.mode]({
    errorMessage,
    meta,
  });

  // add them to the "on" handlers object
  triggers.forEach((t) => {
    if (Array.isArray(on[t])) {
      on[t].push(handleChange);
    } else {
      on[t] = handleChange;
    }
  });

  return on;
});
</script>

<style lang="scss" scoped>


.text-white {
	color: $color-white;
	margin-bottom: .3rem;
}

.has-error {
    color: $color-bv-logo-eld;
}

.error-txt {
	display: block;
	margin:0.5rem 0 0 0;
	color: tint($color-bv-logo-eld, 30%);
}
.light .error-txt {
	display: block;
	margin:0.5rem 0 0 0;
	color: $color-bv-logo-eld;
}

.form-input {
  border-radius: 0.2rem;
  border: 2px solid transparent;
  padding: 0.5rem;
  outline: 1px solid transparent;
  background-color: #f2f5f7;
  width: 100%;
  transition: border-color 0.3s ease-in-out, color 0.3s ease-in-out,
  background-color 0.3s ease-in-out;
}
.form-input:focus {
  outline: solid;
  outline-color:#eed600;
  outline-offset: 2px;
  outline-width: 2px;
}
.light .form-input:focus {
  outline: solid;
  outline-color: $color-bv-logo-blue;
    outline-offset: 2px;
    outline-width: 2px;
}

.light .form-input {
  border-radius: 0.2rem;
  border: 2px solid tint($color-black, 0%);
  padding: 0.5rem;
  outline: 1px solid transparent;
  background-color: $color-white;
  width: 100%;
  transition: border-color 0.3s ease-in-out, color 0.3s ease-in-out,
  background-color 0.3s ease-in-out;
}

input.error:focus {
  border-color: shade($color-bv-logo-eld, 25%);
}

input.error:focus {
  border-color: shade($color-bv-logo-eld, 25%);
}

.form-input.error {
    border:2px solid $color-bv-logo-eld;
    outline: 1px solid #ffffff;
    background:url('./ico-alert.png') no-repeat right, tint($color-bv-logo-eld, 85%);
}

.help-message {
  position: absolute;
  bottom: calc(-1.5 * 1em);
  left: 0;
  margin: 0;
  font-size: 14px;
}
</style>